import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar, Sparkles, Plus, Trash2, Link2, Loader2,
  ArrowLeft, Heart, Gift, Camera, Plane, PartyPopper, GraduationCap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAdminCopy } from "@/contexts/AdminCopyContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OCCASION_TYPES = [
  { id: "wedding", label: "Wedding", Icon: Heart },
  { id: "birthday", label: "Birthday", Icon: Gift },
  { id: "photoshoot", label: "Photoshoot", Icon: Camera },
  { id: "trip", label: "Trip", Icon: Plane },
  { id: "party", label: "Party", Icon: PartyPopper },
  { id: "graduation", label: "Graduation", Icon: GraduationCap },
  { id: "other", label: "Other", Icon: Sparkles },
];

export default function MyOccasionsPage() {
  const navigate = useNavigate();
  const t = useAdminCopy();
  const [occasions, setOccasions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState({ name: "", type: "other", target_date: "", location: "" });

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/me/occasions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setOccasions(data.occasions || []);
    } catch (err) {
      toast.error("Load failed: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Occasion name required");
    const token = localStorage.getItem("session_token");
    try {
      const res = await fetch(`${API}/me/occasions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Occasion created");
      setCreateModal(false);
      setForm({ name: "", type: "other", target_date: "", location: "" });
      load();
    } catch (err) {
      toast.error(String(err.message || err));
    }
  };

  const remove = async (occasion_id) => {
    if (!window.confirm("Delete this occasion? Linked preference cards stay put.")) return;
    const token = localStorage.getItem("session_token");
    try {
      const res = await fetch(`${API}/me/occasions/${occasion_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(String(err.message || err));
    }
  };

  const TypeIcon = (id) => OCCASION_TYPES.find((x) => x.id === id)?.Icon || Sparkles;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="my-occasions-page">
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => navigate("/me")}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <Calendar className="w-5 h-5 text-indigo-600" />
            <div>
              <h1 className="text-lg font-semibold">{t("me.occasions.title", "My Occasions")}</h1>
              <p className="text-xs text-gray-500">{t("me.occasions.subtitle", "Weddings, birthdays, trips, and everything in between")}</p>
            </div>
          </div>
          <Button size="sm" onClick={() => setCreateModal(true)} className="bg-black text-white" data-testid="occasions-create-btn">
            <Plus className="w-4 h-4 mr-1" /> {t("me.occasions.create_cta", "Plan an occasion")}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="text-center py-20 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading...
          </div>
        ) : occasions.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-dashed border-gray-300" data-testid="occasions-empty">
            <Sparkles className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500 mb-3">
              {t("me.occasions.empty", "You haven't planned any occasions yet.")}
            </p>
            <Button onClick={() => setCreateModal(true)} className="bg-black text-white">
              <Plus className="w-4 h-4 mr-1" /> Plan your first occasion
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {occasions.map((o) => {
              const Icon = TypeIcon(o.occasion_type);
              return (
                <div key={o.event_id} className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`occasion-card-${o.event_id}`}>
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-semibold">{o.name}</p>
                        <p className="text-xs text-gray-500">
                          {o.occasion_type} {o.target_date && `· ${o.target_date}`} {o.location && `· ${o.location}`}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => remove(o.event_id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg"
                      data-testid={`occasion-delete-${o.event_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Linked preference cards (the "multi-service" n-to-n linkage) */}
                  <div className="border-t border-gray-100 pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                        <Link2 className="w-3 h-3" /> Linked services ({o.linked_cards?.length || 0})
                      </p>
                      <button
                        onClick={() => navigate(`/me/plan?occasion=${o.event_id}`)}
                        className="text-xs text-indigo-600 hover:underline"
                        data-testid={`occasion-add-service-${o.event_id}`}
                      >
                        + Add a service
                      </button>
                    </div>
                    {o.linked_cards?.length > 0 ? (
                      <ul className="space-y-1.5">
                        {o.linked_cards.map((c) => (
                          <li
                            key={c.card_id}
                            className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                            onClick={() => navigate(`/c/${c.card_id}`)}
                          >
                            <div className="flex items-center gap-2 text-xs">
                              <span className="font-medium">{c.name || "Untitled"}</span>
                              <span className="text-gray-400">· {c.category}</span>
                              <span className="text-gray-400">· {c.status}</span>
                            </div>
                            {c.appointment?.date && (
                              <span className="text-xs text-gray-500">{c.appointment.date}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-gray-400">No services linked yet.</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="create-occasion-modal">
          <form onSubmit={create} className="bg-white rounded-xl p-5 w-full max-w-md">
            <h3 className="font-semibold mb-4">{t("me.occasions.create_title", "Plan an occasion")}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-700">Occasion name</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Sarah's Wedding"
                  className="mt-1"
                  required
                  data-testid="occasion-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Type</label>
                <div className="grid grid-cols-4 gap-1.5 mt-1">
                  {OCCASION_TYPES.map((o) => {
                    const Ico = o.Icon;
                    const sel = form.type === o.id;
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => setForm({ ...form, type: o.id })}
                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border ${sel ? "border-indigo-600 bg-indigo-50 text-indigo-700" : "border-gray-200 hover:bg-gray-50"}`}
                        data-testid={`occasion-type-${o.id}`}
                      >
                        <Ico className="w-4 h-4" />
                        <span className="text-[10px]">{o.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Target date</label>
                <Input type="date" value={form.target_date}
                       onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                       className="mt-1" data-testid="occasion-date-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Location (optional)</label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                       className="mt-1" placeholder="City, venue…" data-testid="occasion-location-input" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button type="button" variant="outline" onClick={() => setCreateModal(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1" data-testid="occasion-create-submit">Create</Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
